import _extends from "@babel/runtime/helpers/extends";
import { Switch, Transition } from "@headlessui/react";
import { CheckIcon, XIcon } from "@heroicons/react/solid";
import classNames from "classnames";
import { noop } from "lodash";
import PropTypes from "prop-types";
import React, { forwardRef } from "react";
import { useSvgAria } from "../../hooks";

/**
 * @param {string} id ID.
 * @param {string|JSX.Element} [as="button"] Base component.
 * @param {boolean} checked Default state.
 * @param {string} screenReaderLabel The label for screen readers.
 * @param {Function} onChange Change callback.
 * @param {boolean} [disabled] Disabled flag.
 * @param {string} [type] Type.
 * @param {string} [className] CSS class.
 * @returns {JSX.Element} Toggle component.
 */
const Toggle = /*#__PURE__*/forwardRef(({
  id,
  as: Component,
  checked,
  screenReaderLabel,
  onChange,
  disabled,
  className,
  type,
  ...props
}, ref) => {
  const svgAriaProps = useSvgAria();
  return /*#__PURE__*/React.createElement(Switch, _extends({
    ref: ref,
    as: Component,
    checked: checked,
    disabled: disabled,
    onChange: disabled ? noop : onChange,
    className: classNames("yst-toggle", checked && "yst-toggle--checked", disabled && "yst-toggle--disabled", className),
    "data-id": id
  }, props, {
    // Force type button when component is button for proper behavior in HTML forms.
    type: Component === "button" ? "button" : type
  }), /*#__PURE__*/React.createElement("span", {
    className: "yst-sr-only"
  }, screenReaderLabel), /*#__PURE__*/React.createElement("span", {
    className: "yst-toggle__handle"
  }, /*#__PURE__*/React.createElement(Transition, {
    show: checked,
    unmount: false,
    as: "span",
    "aria-hidden": !checked,
    enter: "",
    enterFrom: "yst-opacity-0 yst-hidden",
    enterTo: "yst-opacity-100",
    leaveFrom: "yst-opacity-100",
    leaveTo: "yst-opacity-0 yst-hidden"
  }, /*#__PURE__*/React.createElement(CheckIcon, _extends({
    className: "yst-toggle__icon yst-toggle__icon--check"
  }, svgAriaProps))), /*#__PURE__*/React.createElement(Transition, {
    show: !checked,
    unmount: false,
    as: "span",
    "aria-hidden": checked,
    enterFrom: "yst-opacity-0 yst-hidden",
    enterTo: "yst-opacity-100",
    leaveFrom: "yst-opacity-100",
    leaveTo: "yst-opacity-0 yst-hidden"
  }, /*#__PURE__*/React.createElement(XIcon, _extends({
    className: "yst-toggle__icon yst-toggle__icon--x"
  }, svgAriaProps)))));
});
Toggle.displayName = "Toggle";
Toggle.propTypes = {
  as: PropTypes.elementType,
  id: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  screenReaderLabel: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  type: PropTypes.string,
  className: PropTypes.string
};
Toggle.defaultProps = {
  as: "button",
  checked: false,
  disabled: false,
  type: "",
  className: ""
};
export default Toggle;